import { makeStyles } from '@fluentui/react-components'
import { useMsal } from '@azure/msal-react'
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser'
import type {
  FeedbackInitData,
  GetAccessTokenOptions,
  IAccessTokenInfo,
  IFeedbackInitOptions,
  IFeedbackInitOptionsWithTokenCallback,
  TenantCloudType,
} from '@ms/inapp-feedback-loader'
import { useCallback, useEffect, useRef, useState } from 'react'
import { environment } from '../environment'

const useStyles = makeStyles({
  panelHost: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '450px',
    maxWidth: '100vw',
    height: '100vh',
    zIndex: 1000000,
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
})

type FeedbackModule = typeof import('@ms/inapp-feedback-loader')

const parseFeedbackAppId = (value: string): number | undefined => {
  const appId = Number(value)
  return Number.isFinite(appId) && appId > 0 ? appId : undefined
}

const getFeedbackAppId = (): number | undefined =>
  parseFeedbackAppId(
    import.meta.env.PROD
      ? environment.feedbackAppIdProd
      : environment.feedbackAppIdDev,
  )

const getCpsNotApplicableChecks = (
  tenantCloudType: TenantCloudType,
  feedbackModule: FeedbackModule,
) => {
  const { FeedbackPolicyValue } = feedbackModule
  return {
    tenantCloudType,
    policyAllowFeedback: FeedbackPolicyValue.Enabled,
    policyAllowContact: FeedbackPolicyValue.Enabled,
    policyAllowContent: FeedbackPolicyValue.Enabled,
    policyAllowScreenshot: FeedbackPolicyValue.Enabled,
    policyAllowSurvey: FeedbackPolicyValue.NotApplicable,
    policyAllowLimitedFeedback: FeedbackPolicyValue.NotApplicable,
    optionalConnectedExperiences: FeedbackPolicyValue.NotApplicable,
    policyAllowCopilotFeedback: FeedbackPolicyValue.NotApplicable,
    policyEmailDefault: FeedbackPolicyValue.Enabled,
    policyScreenshotDefault: FeedbackPolicyValue.Enabled,
    policyContentSamplesDefault: FeedbackPolicyValue.Enabled,
    policySendPublicFeedback: FeedbackPolicyValue.NotApplicable,
  }
}

const createAccessTokenProvider = (
  instance: IPublicClientApplication,
  account: AccountInfo,
) => {
  return async (
    scopes: string | string[],
  ): Promise<IAccessTokenInfo> => {
    const response = await instance.acquireTokenSilent({
      scopes: Array.isArray(scopes) ? scopes : [scopes],
      account,
    })
    return { token: response.accessToken }
  }
}

const createBaseFeedbackOptions = (
  appId: number,
  account: AccountInfo,
  tenantCloudType: TenantCloudType,
  getAccessToken: (
    scopes: string | string[],
    options?: GetAccessTokenOptions,
  ) => Promise<IAccessTokenInfo>,
  dismissHandler: () => void,
  feedbackModule: FeedbackModule,
): IFeedbackInitOptionsWithTokenCallback => {
  const {
    FeedbackAgeGroup,
    FeedbackAuthenticationType,
    FeedbackHostPlatformType,
    FeedbackScreenshotInfoImageFormat,
    FeedbackScreenshotSourceType,
    FeedbackUiType,
  } = feedbackModule

  return {
    appId,
    tenantId: account.tenantId,
    userId: `a:${account.localAccountId}`,
    isProduction: import.meta.env.PROD,
    authenticationType: FeedbackAuthenticationType.AAD,
    ageGroup: FeedbackAgeGroup.Adult,
    callbackFunctions: {
      onDismiss: dismissHandler,
      getAccessToken,
    },
    cloudPolicyServiceConfig: {
      callerAppHint: environment.feedbackHostName,
    },
    themeOptions: {
      isFluentV9: true,
    },
    feedbackConfig: {
      scenarioConfig: {
        isScenarioEnabled: false,
      },
      email: account.username,
      feedbackUiType: FeedbackUiType.SidePane,
      isDisplayed: true,
      hostPlatform: FeedbackHostPlatformType.Web,
      invokeOnDismissOnEsc: true,
      isFeedbackForumEnabled: false,
      isMyFeedbackEnabled: false,
      isScreenRecordingEnabled: true,
      isScreenshotEnabled: true,
      isThankYouPageDisabled: false,
      isFileUploadEnabled: true,
      isEmailCollectionEnabled: true,
      screenshotInfo: {
        providedScreenshotType:
          FeedbackScreenshotSourceType.AutoCaptureWithHtml2Canvas,
        screenshotImageFormat: FeedbackScreenshotInfoImageFormat.PNG,
      },
      complianceChecks: {
        tenantCloudType,
      },
    },
  }
}

const resolveTenantCloudType = async (
  feedbackModule: FeedbackModule,
  appId: number,
  account: AccountInfo,
): Promise<TenantCloudType> => {
  const { FeedbackAuthenticationType, TenantCloudType, getTenantCloudType } =
    feedbackModule

  try {
    return (
      (await getTenantCloudType({
        appId,
        authenticationType: FeedbackAuthenticationType.AAD,
        tenantId: account.tenantId,
        userId: `a:${account.localAccountId}`,
        isProduction: import.meta.env.PROD,
      })) ?? TenantCloudType.Worldwide
    )
  } catch (error) {
    console.warn(
      '[shell] Unable to resolve feedback tenant cloud type; using Worldwide',
      error,
    )
    return TenantCloudType.Worldwide
  }
}

const runNonCpsFallback = async (
  baseOptions: IFeedbackInitOptionsWithTokenCallback,
  tenantCloudType: TenantCloudType,
  feedbackModule: FeedbackModule,
): Promise<IFeedbackInitOptions> => {
  const fallbackOptions = {
    ...baseOptions,
    feedbackConfig: {
      ...baseOptions.feedbackConfig,
      complianceChecks: getCpsNotApplicableChecks(
        tenantCloudType,
        feedbackModule,
      ),
    },
  }
  const { init } = await feedbackModule.canDisplayFeedbackNonCPS(
    fallbackOptions as Parameters<
      typeof feedbackModule.canDisplayFeedbackNonCPS
    >[0],
  )
  return init
}

const launchForHost = async (
  hostElement: Element,
  appId: number,
  options: IFeedbackInitOptions,
  feedbackModule: FeedbackModule,
): Promise<void> => {
  const feedbackOptions = {
    ...options,
    clientName: environment.feedbackHostName,
  }
  let initializationError: unknown | null = null
  const feedbackInfo: FeedbackInitData = {
    hostName: environment.feedbackHostName,
    locale: environment.feedbackLocale,
    inAppFeedbackRef: hostElement,
    useCentroConfig: true,
    iFrameAppId: String(appId),
    onInitializationError: (error) => {
      initializationError = error
    },
  }

  await feedbackModule.launchFeedback(feedbackInfo, feedbackOptions)

  if (initializationError) {
    throw initializationError instanceof Error
      ? initializationError
      : new Error(String(initializationError))
  }

  feedbackModule.updateFeedbackOptions(feedbackOptions)
}

interface FeedbackPanelProps {
  launchRequestId: number
}

export const FeedbackPanel = ({ launchRequestId }: FeedbackPanelProps) => {
  const styles = useStyles()
  const { instance, accounts } = useMsal()
  const latestLaunchIdRef = useRef(0)
  const isLaunchingRef = useRef(false)
  const feedbackHostRef = useRef<HTMLDivElement | null>(null)
  const unmountFeedbackRef = useRef<(() => void) | null>(null)
  const [dismissedLaunchRequestId, setDismissedLaunchRequestId] = useState(0)
  const isOpen = launchRequestId > dismissedLaunchRequestId

  const handleDismiss = useCallback(() => {
    setDismissedLaunchRequestId(launchRequestId)
    unmountFeedbackRef.current?.()
    unmountFeedbackRef.current = null
  }, [launchRequestId])

  useEffect(() => {
    if (
      !isOpen ||
      launchRequestId <= 0 ||
      launchRequestId === latestLaunchIdRef.current ||
      isLaunchingRef.current
    ) {
      return
    }

    let cancelled = false

    const launch = async () => {
      isLaunchingRef.current = true

      try {
        const appId = getFeedbackAppId()
        if (!appId) {
          throw new Error('Feedback app ID is not configured.')
        }

        const account = accounts[0]
        if (!account?.tenantId || !account.localAccountId) {
          throw new Error('The signed-in account is unavailable for feedback.')
        }

        const hostElement = feedbackHostRef.current
        if (!hostElement) {
          throw new Error('Feedback host is not available.')
        }

        const feedbackModule = await import('@ms/inapp-feedback-loader')
        const tenantCloudType = await resolveTenantCloudType(
          feedbackModule,
          appId,
          account,
        )
        const baseOptions = createBaseFeedbackOptions(
          appId,
          account,
          tenantCloudType,
          createAccessTokenProvider(instance, account),
          handleDismiss,
          feedbackModule,
        )

        let launchOptions: IFeedbackInitOptions
        try {
          const result =
            await feedbackModule.canDisplayUserFeedback(baseOptions)
          launchOptions = result.canDisplay
            ? {
                ...result.init,
                feedbackConfig: {
                  ...result.init.feedbackConfig,
                  complianceChecks: getCpsNotApplicableChecks(
                    tenantCloudType,
                    feedbackModule,
                  ),
                },
              }
            : await runNonCpsFallback(
                baseOptions,
                tenantCloudType,
                feedbackModule,
              )
        } catch {
          launchOptions = await runNonCpsFallback(
            baseOptions,
            tenantCloudType,
            feedbackModule,
          )
        }

        await launchForHost(
          hostElement,
          appId,
          launchOptions,
          feedbackModule,
        )
        unmountFeedbackRef.current = feedbackModule.unmountFeedback

        if (!cancelled) {
          latestLaunchIdRef.current = launchRequestId
        }
      } catch (error) {
        console.error('[shell] Unable to launch feedback', error)
        if (!cancelled) {
          handleDismiss()
          window.alert(
            error instanceof Error
              ? `Feedback launch failed: ${error.message}`
              : 'Feedback launch failed.',
          )
        }
      } finally {
        isLaunchingRef.current = false
      }
    }

    void launch()

    return () => {
      cancelled = true
    }
  }, [
    accounts,
    handleDismiss,
    instance,
    isOpen,
    launchRequestId,
  ])

  useEffect(
    () => () => {
      unmountFeedbackRef.current?.()
      unmountFeedbackRef.current = null
    },
    [],
  )

  if (!isOpen) {
    return null
  }

  return (
    <div
      id="ocv-feedback-host"
      ref={feedbackHostRef}
      className={styles.panelHost}
      role="region"
      aria-label="Feedback panel"
    />
  )
}
